import { Platform, StyleSheet } from 'react-native';
import { isTelevisionRuntime } from '../utils/platformCapabilities';

// Page-shell font — matches mature Jellyflix's "Helvetica Neue", Helvetica,
// Arial, sans-serif stack used for navigation, cards, shelves, item details,
// and every non-Player surface.
export const systemSansFont = Platform.select({
    web: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    ios: 'System',
    android: 'HelveticaNeue',
    default: 'System',
}) as string;

// Player font — matches mature Jellyflix's ui-sans-serif/system-ui stack.
// The Player deliberately uses a different face from the page shell.
export const playerFont = Platform.select({
    web: 'ui-sans-serif, system-ui, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
    ios: 'System',
    android: 'sans-serif',
    default: 'System',
}) as string;

export const colors = {
    background: '#000000',
    page: '#050505',
    surface: '#0d0d0d',
    surfaceRaised: '#171717',
    input: '#151515',
    brand: '#e50914',
    brandBright: '#ff4a55',
    text: '#ffffff',
    textStrong: '#f5f5f5',
    textMuted: 'rgba(255,255,255,0.68)',
    textFaint: 'rgba(255,255,255,0.52)',
    border: 'rgba(255,255,255,0.14)',
    brandBorder: 'rgba(229,9,20,0.38)',
    scrim: 'rgba(0,0,0,0.72)',
    danger: '#ef4444',
} as const;

export const spacing = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32, page: 56 } as const;
export const radius = { small: 6, medium: 8, large: 12, pill: 999 } as const;
export const motion = {
    interaction: 150,
    controlEnter: 130,
    controlExit: 90,
    controlPress: 55,
    imageReveal: 180,
    sectionReveal: 220,
    heroTransition: 400,
} as const;
export const interaction = {
    focusScale: 1.035,
    focusBorderWidth: 2,
    focusEnter: 150,
    focusExit: 95,
    raisedElevation: 8,
} as const;

export type BrowseViewport = 'compact' | 'desktop' | 'television';

export type BrowseVisualTokens = {
    viewport: BrowseViewport;
    pagePadding: number;
    navFontSize: number;
    navLineHeight: number;
    shelfTitleSize: number;
    shelfTitleLineHeight: number;
    heroTitleSize: number;
    heroTitleLineHeight: number;
    heroMetadataSize: number;
    heroOverviewSize: number;
    heroOverviewLineHeight: number;
    heroActionHeight: number;
    heroActionFontSize: number;
    heroContentWidth: number;
};

/**
 * Shared browse-shell scale. Native television runtimes deliberately win over
 * viewport width so a large desktop monitor does not silently become a TV UI.
 */
export function getBrowseVisualTokens(width: number): BrowseVisualTokens {
    if (isTelevisionRuntime()) {
        return {
            viewport: 'television',
            pagePadding: 64,
            navFontSize: 18,
            navLineHeight: 23,
            shelfTitleSize: 30,
            shelfTitleLineHeight: 38,
            heroTitleSize: 64,
            heroTitleLineHeight: 66,
            heroMetadataSize: 17,
            heroOverviewSize: 19,
            heroOverviewLineHeight: 29,
            heroActionHeight: 56,
            heroActionFontSize: 19,
            heroContentWidth: 760,
        };
    }
    if (width <= 760) {
        return {
            viewport: 'compact',
            pagePadding: 16,
            navFontSize: 12,
            navLineHeight: 18,
            shelfTitleSize: 19,
            shelfTitleLineHeight: 25,
            heroTitleSize: 38,
            heroTitleLineHeight: 41,
            heroMetadataSize: 13,
            heroOverviewSize: 13,
            heroOverviewLineHeight: 19,
            heroActionHeight: 44,
            heroActionFontSize: 15,
            heroContentWidth: 350,
        };
    }
    return {
        viewport: 'desktop',
        pagePadding: width >= 1280 ? 56 : 32,
        navFontSize: 15,
        navLineHeight: 20,
        shelfTitleSize: 25,
        shelfTitleLineHeight: 33,
        heroTitleSize: 56,
        heroTitleLineHeight: 58,
        heroMetadataSize: 14,
        heroOverviewSize: 16,
        heroOverviewLineHeight: 24,
        heroActionHeight: 46,
        heroActionFontSize: 17,
        heroContentWidth: 680,
    };
}

export const typography = StyleSheet.create({
    body: { color: colors.text, fontFamily: systemSansFont, fontSize: 14 },
    muted: { color: colors.textMuted, fontFamily: systemSansFont, fontSize: 13 },
    sectionTitle: { color: colors.text, fontFamily: systemSansFont, fontSize: 22, fontWeight: '800' },
    title: { color: colors.text, fontFamily: systemSansFont, fontSize: 38, lineHeight: 40, fontWeight: '900' },
    brand: { color: colors.brand, fontFamily: systemSansFont, fontSize: 18, fontWeight: '900' },
});
